import moment from "moment";
import { X, Edit2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";

import Backend from "../../../../Backend/Backend";
import LTP from "./LTP";
import { useNavigate } from "react-router-dom";

const backend = await new Backend().init();

export default function PendingCard({ item, reload }) {
  const invester = Number(localStorage.getItem("invester"));

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  // ✅ Base payload for API
  const baseData = useMemo(
    () => ({
      server: localStorage.getItem("server"),
      user_id: localStorage.getItem("id"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
    }),
    [],
  );

  // ✅ Cancel Pending Order
  const handleRequestCancel = useCallback(
    async (data) => {
      try {
        setLoading(true);
        const res = await backend.delete_pending(data);
        if (res.error === "False") {
          toast.success(res.message || "Pending order cancelled");
          reload?.();
        } else {
          toast.error(res.message || "Cancel request failed");
        }
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
        setShowConfirmCancel(false);
      }
    },
    [reload],
  );

  const handleCancel = useCallback(() => {
    const data = {
      ...baseData,
      pending_id: item.id,
      user: "1",
    };
    handleRequestCancel(data);
  }, [item.id, baseData, handleRequestCancel]);

  const onModify = () => {
    window.symboldatapending = item;
    localStorage.setItem("symboldatapending", JSON.stringify(item));
    navigate("/BuySellPending");
  };

  const isLimitType = item.limit_sl === 0 ? "TAKE PROFIT" : "TAKE LOSS";
  const isLimitTypeColor =
    item.limit_sl === 0 ? "text-[var(--buymeta)]" : "text-[var(--sellmeta)]";

  return (
    <div className="flex justify-between items-center py-2 border-b border-[var(--hover)] last:border-0 hover:bg-[var(--hover)]/5 transition px-1">
      {/* Left Side */}
      <div className="flex-1">
        <span className="font-semibold large text-[var(--text)]">
          {item.symbol}
        </span>
        <span className="font-semibold ml-1 text-[9px] text-[var(--text)]">
          {moment(item.expiry_date).format("DD-MMM")}.
        </span>

        <div className={`text-[12px] ${isLimitTypeColor} font-semibold`}>
          {isLimitType}
        </div>

        {/* Buttons */}
        <div className="flex gap-2 mt-1">
          {/* ✅ Modify */}
          <button
            disabled={loading || item.status > 0}
            onClick={() => (invester === 1 ? null : onModify())}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-[var(--buymeta)] text-[var(--white)] hover:opacity-90 disabled:opacity-50 transition"
          >
            <Edit2 size={12} /> Modify
          </button>

          {/* ✅ Cancel */}
          <button
            disabled={loading || item.status > 0}
            onClick={() => (invester === 1 ? null : setShowConfirmCancel(true))}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-[var(--sellmeta)] text-[var(--white)] hover:opacity-90 disabled:opacity-50 transition"
          >
            <X size={12} /> Cancel
          </button>
        </div>
      </div>

      {/* Right Side */}
      <div className="text-right large font-semibold text-[var(--text)]">
        <span>
          <span
            className={`${
              item.buy_sell === 0
                ? "text-[var(--buymeta)]"
                : "text-[var(--sellmeta)]"
            }`}
          >
            {item.buy_sell === 0 ? "BUY" : "SELL"},{" "}
          </span>
          {parseFloat(item.qty)} @ {parseFloat(item.price)}
        </span>

        <div
          className={`${item.status === 0 ? "text-[var(--text)]" : item.status === 1 ? "text-[var(--buymeta)]" : "text-[var(--sellmeta)]"} text-[12px] mt-1 mb-1`}
        >
          {item.status === 0 ? <LTP symbol_id={item.symbol_id} /> : item.msg}
        </div>
        {item.status === 0 ? null : (
          <div className="text-[10px] text-[var(--subtext)] font-semibold">
            {item.status === 1
              ? `${item.buy_sell === 0 ? "Buy" : "Sell"} Time `
              : item.status === 2
                ? "Reject Time "
                : "Create Time"}
            {item.status === 1
              ? moment(item.time_buy).format("DD-MM-YYYY HH:mm:ss")
              : item.status === 2
                ? moment(item.date_cancel).format("DD-MM-YYYY HH:mm:ss")
                : null}
          </div>
        )}

        <div className="text-[10px] text-[var(--subtext)] font-semibold">
          Create Time :{" "}
          {moment(item.date_created).format("DD-MM-YYYY HH:mm:ss")}
        </div>
      </div>

      {/* Confirm Cancel Modal */}
      {showConfirmCancel && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[var(--background)] p-5 rounded-2xl shadow-lg w-[90%] max-w-sm text-center border border-[var(--hover)]">
            <h3 className="text-lg font-semibold text-[var(--text)] mb-2">
              Confirm Cancel
            </h3>
            <p className="text-sm text-[var(--subtext)] mb-4">
              Are you sure you want to cancel <b>{item.symbol}</b> pending
              order?
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={handleCancel}
                disabled={loading}
                className="bg-[var(--sellmeta)] text-white px-4 py-2 rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
              >
                {loading ? "Cancelling..." : "Yes, Cancel"}
              </button>
              <button
                onClick={() => setShowConfirmCancel(false)}
                className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
